import { css } from '@emotion/react';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import {
  ArcElement, BarElement, CategoryScale, Chart as ChartJS, type ChartOptions, Filler, Legend, LinearScale, LineElement,
  PointElement, RadialLinearScale, Tooltip,
} from 'chart.js';
import {
  type FC, useMemo, useState,
} from 'react';
import {
  Bar, Doughnut, Pie, PolarArea, Radar,
} from 'react-chartjs-2';
import { Trans } from 'react-i18next';
import {
  DropDownItemSize, type DropdownOption, RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { InputSize } from '~/_shared/baseComponents/inputs';
import { Inline } from '~/_shared/baseComponents/layout/inline.component';
import { Stack } from '~/_shared/baseComponents/layout/stack.component';
import { rgbColorToString } from '~/_shared/components/colorPicker/colorPicker.helpers';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { compareSpreadsheetColumnIds } from '~/_shared/types/spreadsheetData/spreadsheetColumns.helpers';
import {
  isNullsy, notNullsy,
} from '~/_shared/utils/typeGuards';
import { useTheme } from '../_shared/themes/theme.hooks';
import type { Theme } from '../_shared/themes/theme.model';
import { useTranslation } from '../_shared/utils/hooks';
import { type PieChartItem } from '../_shared/utils/maptive/pieCharts/pieCharts.types';
import { CHART_OPTIONS_STYLE_BY_TYPE } from './chartsVisualizer.helpers';
import {
  type ChartsInfo, MaptiveChartType, type MetricOption, MetricOptionKind,
} from './chartsVisualizer.types';

ChartJS.register(ArcElement, Tooltip, Legend, RadialLinearScale, PointElement, Filler, LineElement, CategoryScale,
  LinearScale,
  BarElement,);

const DROPDOWN_LABEL_GAP = 6;

const PLACEHOLDER_OPTIONS_OVERRIDE = {
  animation: false,
  plugins: {
    legend: { display: false },
    tooltip: { enabled: false },
  },
} as const;

const RADIAL_PLACEHOLDER_DATA: ChartsInfo = {
  labels: [''],
  datasets: [{
    label: '',
    data: [1],
    backgroundColor: ['#cccccc'],
    borderColor: ['#aaaaaa'],
    borderWidth: 1,
  }],
};

const CARTESIAN_PLACEHOLDER_DATA: ChartsInfo = {
  labels: [],
  datasets: [{
    label: '',
    data: [],
    backgroundColor: [],
    borderColor: [],
    borderWidth: 1,
  }],
};

const dropdownStyle = css({
  width: 236,
});

const contentWrapperStyle = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  minHeight: 535,
  padding: '16px 16px 28px',
});

const dropdownLabelStyle = css({
  fontSize: 16,
  textTransform: 'capitalize',
});

const chartAndTotalWrapperStyle = css({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  justifyContent: 'space-between',
  position: 'relative',
});

const chartSectionStyle = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: 450,
  width: '100%',
});

const chartHolderStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  minHeight: 0,
  width: 440,
});

const dataUnavailableWarningStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: '8px 0',
  width: '100%',
});

const dataUnavailableHeaderStyle = (theme: Theme) => css({
  alignItems: 'center',
  color: theme.textColors.danger,
  display: 'flex',
  fontSize: 18,
  fontWeight: 'bold',
  gap: 8,
  justifyContent: 'center',
});

const warningIconStyle = css({
  fontSize: 16,
});

const dataUnavailableDescriptionStyle = (theme: Theme) => css({
  color: theme.textColors.danger,
  fontSize: 14,
  margin: 0,
  textAlign: 'center',
});

const CHART_FRAME = 4;
const chartLoaderOverlayStyle = css({
  borderRadius: 4,
  bottom: -CHART_FRAME,
  top: CHART_FRAME,
});

const totalLabelStyle = (theme: Theme) => css({
  alignSelf: 'center',
  backgroundColor: theme.backgroundColors.highlight,
  border: `1px solid ${theme.borderColors.activeItem}`,
  borderRadius: 9999,
  color: theme.textColors.primary,
  fontSize: 16,
  marginTop: 12,
  padding: '4px 12px',
});

type ChartsVisualizerComponentProps = Readonly<{
  allOptions: ReadonlyArray<MetricOption>;
  data: {
    chartItems: ReadonlyArray<PieChartItem> | null;
    secondaryChartItems: ReadonlyArray<PieChartItem> | null;
  } | null;
  isChartDataLoading: boolean;
  isMetricLoading: boolean;
  onSelectedMetricChange: (metric: MetricOption | null) => void;
  primarySourceLabel: string;
  secondarySourceLabel?: string;
  selectedMetric: MetricOption | null;
}>;

export const ChartsVisualizerComponent: FC<ChartsVisualizerComponentProps> = props => {
  const [selectedChartType, setSelectedChartType] = useState<MaptiveChartType>(MaptiveChartType.DoughnutChart);
  const [hierarchy, setHierarchy] = useState(0);
  const [t] = useTranslation();
  const theme = useTheme();

  const chartTypes: DropdownOption<MaptiveChartType>[] = useMemo(() => Object.keys(MaptiveChartType)
    .sort((s1, s2) => s1 > s2 ? 1 : -1)
    .map((chartName: MaptiveChartType) => ({
      name: t(chartName),
      value: chartName,
    })),
  [t]);

  const metricOptions: DropdownOption<MetricOption>[] = useMemo(() => (
    props.allOptions.map(option => ({
      name: option.name,
      value: option,
    }))
  ), [props.allOptions]);

  const hasSecondaryGrouping = notNullsy(props.secondarySourceLabel);
  const isSecondaryHierarchy = hasSecondaryGrouping && hierarchy === 1;

  const chartItems = props.data?.chartItems
    ? isSecondaryHierarchy ? props.data?.secondaryChartItems : props.data?.chartItems
    : null;

  const sourceLabel = isSecondaryHierarchy ? props.secondarySourceLabel : props.primarySourceLabel;

  const data: ChartsInfo | null = useMemo(() => {
    if (!chartItems) {
      return null;
    }
    const borderColors: string[] = [];
    const colors: string[] = [];
    const labels: string[] = [];
    const values: number[] = [];

    chartItems.forEach(item => {
      borderColors.push(rgbColorToString({ ...item.rgbColor, a: 1 }));
      colors.push(rgbColorToString({ ...item.rgbColor, a: item.rgbColor.a === 1 ? 0.8 : item.rgbColor.a }));
      labels.push(item.title);
      values.push(item.count);
    });

    return {
      datasets: [{
        backgroundColor: colors,
        borderColor: borderColors,
        borderWidth: 1,
        data: values,
        label: sourceLabel,
      }],
      labels,
    };
  }, [chartItems, sourceLabel]);

  const totalCount = useMemo(() => {
    const count = chartItems?.reduce((sum, item) => sum + item.count, 0);
    return isNullsy(count) ? 'N/A' : count.toLocaleString();
  }, [chartItems]);

  const isCartesian = selectedChartType === MaptiveChartType.VerticalBarChart
    || selectedChartType === MaptiveChartType.HorizontalBarChart;

  const displayData = data ?? (isCartesian ? CARTESIAN_PLACEHOLDER_DATA : RADIAL_PLACEHOLDER_DATA);

  const placeholderOverride = data ? undefined : PLACEHOLDER_OPTIONS_OVERRIDE;

  const chartOptions = useMemo(() => (
    { ...CHART_OPTIONS_STYLE_BY_TYPE[selectedChartType]({ theme }), ...placeholderOverride }
  ), [selectedChartType, theme, placeholderOverride]);

  const metricOptionComparator = (a: MetricOption | null, b: MetricOption | null) => {
    if (!a || !b || a.kind !== b.kind) {
      return false;
    }
    if (a.kind === MetricOptionKind.LocationCount) {
      return true;
    }

    return b.kind === MetricOptionKind.Column && compareSpreadsheetColumnIds(a, b);
  };

  return (
    <div css={contentWrapperStyle}>
      <Inline gap={20}>
        <Stack gap={DROPDOWN_LABEL_GAP}>
          <span css={dropdownLabelStyle}>
            {t('Select a chart type')}
          </span>
          <RegularDropdownComponent
            css={dropdownStyle}
            inputSize={InputSize.Medium}
            itemSize={DropDownItemSize.Large}
            onChange={setSelectedChartType}
            options={chartTypes}
            value={selectedChartType}
          />
        </Stack>
        {hasSecondaryGrouping && (
          <Stack gap={DROPDOWN_LABEL_GAP}>
            <span css={dropdownLabelStyle}>
              {t('Select Source')}
            </span>
            <RegularDropdownComponent
              css={dropdownStyle}
              inputSize={InputSize.Medium}
              itemSize={DropDownItemSize.Large}
              onChange={setHierarchy}
              options={[{ name: t('Primary Group'), value: 0 }, { name: t('Secondary Group'), value: 1 }]}
              value={hierarchy}
            />
          </Stack>
        )}
        <Stack gap={DROPDOWN_LABEL_GAP}>
          <span css={dropdownLabelStyle}>
            {t('Select a Metric')}
          </span>
          <RegularDropdownComponent
            compareOptions={metricOptionComparator}
            css={dropdownStyle}
            inputSize={InputSize.Medium}
            isLoading={props.isMetricLoading}
            itemSize={DropDownItemSize.Large}
            onChange={props.onSelectedMetricChange}
            options={metricOptions}
            value={props.selectedMetric}
          />
        </Stack>
      </Inline>

      <div css={chartAndTotalWrapperStyle}>
        <div css={chartSectionStyle}>
          {!data && !props.isChartDataLoading && (
            <div css={dataUnavailableWarningStyle}>
              <div css={dataUnavailableHeaderStyle}>
                <FontAwesomeIcon css={warningIconStyle} icon={faExclamationTriangle} />
                {t('chartsVisualizer.dataUnavailable.header')}
              </div>
              <p css={dataUnavailableDescriptionStyle}>
                {t('chartsVisualizer.dataUnavailable.description')}
              </p>
            </div>
          )}
          <div css={chartHolderStyle}>
            {selectedChartType === MaptiveChartType.DoughnutChart && (
              <Doughnut
                data={displayData}
                options={chartOptions as ChartOptions<'doughnut'>}
              />
            )}
            {selectedChartType === MaptiveChartType.PieChart && (
              <Pie
                data={displayData}
                options={chartOptions as ChartOptions<'pie'>}
              />
            )}
            {selectedChartType === MaptiveChartType.PolarAreaChart && (
              <PolarArea
                data={displayData}
                options={chartOptions as ChartOptions<'polarArea'>}
              />
            )}
            {selectedChartType === MaptiveChartType.RadarChart && (
              <Radar
                data={displayData}
                options={chartOptions as ChartOptions<'radar'>}
              />
            )}
            {selectedChartType === MaptiveChartType.VerticalBarChart && (
              <Bar
                data={displayData}
                options={chartOptions as ChartOptions<'bar'>}
              />
            )}
            {selectedChartType === MaptiveChartType.HorizontalBarChart && (
              <Bar
                data={displayData}
                options={chartOptions as ChartOptions<'bar'>}
              />
            )}
          </div>
        </div>

        <Inline gap={16} css={totalLabelStyle}>
          <Trans
            components={{ bold: <strong /> }}
            i18nKey="chartsVisualizer.totalLabel"
            parent="span"
            values={{
              name: props.selectedMetric?.kind === MetricOptionKind.LocationCount || !props.selectedMetric
                ? t('Locations')
                : props.selectedMetric.name,
            }}
          />
          <span>{totalCount}</span>
        </Inline>

        {props.isChartDataLoading && <OverlayLoaderComponent css={chartLoaderOverlayStyle} />}
      </div>

    </div>
  );
};
